import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    IconButton,
    Chip,
    Alert,
} from '@mui/material';
import { Add, Delete, Edit, ExpandLess, ExpandMore, Psychology } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { fetchAllowedLlms, fetchLicenseLlmCatalog } from '../services/licensingApi';

const getBackendBase = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/?$/, '/');

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
    };
};

const getTenantId = () => localStorage.getItem('tenantId') || '1';

const stringifyJsonField = (val) => {
    if (val == null || val === '') return '{}';
    if (typeof val === 'object') return JSON.stringify(val, null, 2);
    return String(val);
};

const normalizeModelConfigRow = (r) => {
    if (!r || typeof r !== 'object') return r;
    return {
        ...r,
        modelConfigurationId: r.modelConfigurationId ?? r.model_configuration_id,
        displayName: r.displayName ?? r.display_name ?? '',
        invocationKind: r.invocationKind ?? r.invocation_kind ?? '',
        connectionConfig: r.connectionConfig ?? r.connection_config,
        isActive: r.isActive !== false && r.is_active !== false,
        hasApiSecret: r.hasApiSecret ?? r.has_api_secret ?? false,
    };
};

/** Normalize Master LLM / allowed-llm rows into one shape for the dropdown. */
const normalizeLlmOption = (r) => {
    if (!r || typeof r !== 'object') return null;
    const provider = r.provider || '';
    const modelCode = r.model_code || r.modelCode || '';
    if (!modelCode) return null;
    return {
        masterLlmId: r.master_llm_id || r.masterLlmId || null,
        provider,
        modelCode,
        displayName:
            r.display_name ||
            r.displayName ||
            `${provider || 'provider'} / ${modelCode}`,
        invocationKind: (r.invocation_kind || r.invocationKind || '').trim(),
        defaultConnectionConfig:
            r.default_connection_config || r.defaultConnectionConfig || null,
    };
};

const emptyForm = () => ({
    displayName: '',
    invocationKind: '',
    modelKey: '',
    connectionConfig: '{}',
    apiSecret: '',
    showSecretMask: false,
    isActive: true,
});

const llmOptionKey = (row) => `${row.provider || ''}::${row.modelCode || ''}`;

const buildConfigFromMaster = (match) => {
    if (!match) return {};
    let cfg = {
        ...(match.provider ? { provider: match.provider } : {}),
        ...(match.modelCode ? { model: match.modelCode } : {}),
    };
    const rawDef = match.defaultConnectionConfig;
    if (rawDef) {
        try {
            const def = typeof rawDef === 'string' ? JSON.parse(rawDef) : rawDef;
            if (def && typeof def === 'object' && !Array.isArray(def)) {
                cfg = { ...def, ...cfg };
            }
        } catch {
            /* ignore */
        }
    }
    return cfg;
};

const SECRET_MASK_PLACEHOLDER = '••••••••••••';

const ModelConfigurationAdmin = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [masterCatalog, setMasterCatalog] = useState([]);
    const [allowedLlms, setAllowedLlms] = useState([]);
    const [catalogLoaded, setCatalogLoaded] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);

    const listUrl = useCallback(() => {
        const tid = getTenantId();
        return `${getBackendBase()}asrosync/model-configurations/tenant/${encodeURIComponent(tid)}`;
    }, []);

    /**
     * Dropdown = Licensed LLMs only (same idea as licensed connectors / flows).
     * Empty allowlist → empty dropdown (ask CDX to add under License), never the full Master catalog.
     */
    const dropdownOptions = useMemo(() => {
        const catalog = masterCatalog.map(normalizeLlmOption).filter(Boolean);
        const byKey = new Map(catalog.map((o) => [llmOptionKey(o), o]));

        return (allowedLlms || [])
            .map((raw) => {
                const n = normalizeLlmOption(raw);
                if (!n) return null;
                const fromMaster = byKey.get(llmOptionKey(n));
                if (!fromMaster) {
                    return n;
                }
                return {
                    ...fromMaster,
                    displayName: fromMaster.displayName || n.displayName,
                    invocationKind: fromMaster.invocationKind || n.invocationKind,
                    defaultConnectionConfig:
                        fromMaster.defaultConnectionConfig || n.defaultConnectionConfig,
                };
            })
            .filter(Boolean);
    }, [masterCatalog, allowedLlms]);

    const hasLicensedLlms = dropdownOptions.length > 0;

    const loadCatalogs = useCallback(async () => {
        try {
            const [cat, allowed] = await Promise.all([
                fetchLicenseLlmCatalog().catch(() => []),
                fetchAllowedLlms(getTenantId()).catch(() => []),
            ]);
            setMasterCatalog(Array.isArray(cat) ? cat : []);
            setAllowedLlms(Array.isArray(allowed) ? allowed : []);
        } catch (e) {
            console.warn(e);
            setMasterCatalog([]);
            setAllowedLlms([]);
        } finally {
            setCatalogLoaded(true);
        }
    }, []);

    const fetchRows = async () => {
        setLoading(true);
        try {
            const res = await fetch(listUrl(), { method: 'GET', headers: getAuthHeaders() });
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();
            setRows(Array.isArray(data) ? data.map((row) => normalizeModelConfigRow(row)) : []);
        } catch (e) {
            console.error(e);
            toast.error('Failed to load model configurations');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRows();
        loadCatalogs();
    }, [listUrl, loadCatalogs]);

    const handleField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const applyModel = (key) => {
        const match = dropdownOptions.find((r) => llmOptionKey(r) === key);
        const cfg = buildConfigFromMaster(match);
        const inv = (match?.invocationKind || '').trim();
        setForm((prev) => {
            const prevMatch = dropdownOptions.find((r) => llmOptionKey(r) === prev.modelKey);
            const prevPretty = prevMatch?.displayName || '';
            return {
                ...prev,
                modelKey: key,
                invocationKind: inv || prev.invocationKind,
                connectionConfig: JSON.stringify(cfg, null, 2),
                displayName:
                    !prev.displayName || prev.displayName === prevPretty
                        ? match?.displayName || prev.displayName
                        : prev.displayName,
            };
        });
    };

    const openCreate = () => {
        setEditingId(null);
        setShowAdvanced(false);
        if (dropdownOptions.length === 0) {
            toast.warning(
                'No Licensed LLMs for this tenant. Ask CDX to add under License → Licensed LLMs, then Save.'
            );
            return;
        }
        const first = dropdownOptions[0];
        const key = llmOptionKey(first);
        setForm({
            ...emptyForm(),
            modelKey: key,
            invocationKind: first.invocationKind || '',
            connectionConfig: JSON.stringify(buildConfigFromMaster(first), null, 2),
            displayName: first.displayName || '',
        });
        setDialogOpen(true);
    };

    const openEdit = async (row) => {
        const n = normalizeModelConfigRow(row);
        const mid = n.modelConfigurationId;
        if (mid == null || mid === '') {
            toast.error('Missing configuration id; cannot edit.');
            return;
        }
        const tid = getTenantId();
        setEditingId(Number(mid));
        setShowAdvanced(false);
        let detail = n;
        try {
            const res = await fetch(
                `${getBackendBase()}asrosync/model-configurations/tenant/${encodeURIComponent(tid)}/${encodeURIComponent(mid)}`,
                { headers: getAuthHeaders() }
            );
            if (res.ok) {
                detail = normalizeModelConfigRow(await res.json());
            }
        } catch (e) {
            console.warn('Load model configuration detail failed; using list row', e);
        }
        const cfg =
            detail.connectionConfig && typeof detail.connectionConfig === 'object'
                ? detail.connectionConfig
                : {};
        const model = cfg.model || cfg.model_code || '';
        const provider = cfg.provider || '';
        let modelKey = '';
        if (model) {
            const hit = dropdownOptions.find((r) => {
                const m = (r.modelCode || '').toLowerCase();
                const p = (r.provider || '').toLowerCase();
                return (
                    m === String(model).toLowerCase() &&
                    (!provider || !p || p === String(provider).toLowerCase())
                );
            });
            modelKey = hit ? llmOptionKey(hit) : '';
        }
        setForm({
            displayName: detail.displayName || '',
            invocationKind: detail.invocationKind || '',
            modelKey,
            connectionConfig: stringifyJsonField(detail.connectionConfig),
            apiSecret: '',
            showSecretMask: !!detail.hasApiSecret,
            isActive: detail.isActive !== false,
        });
        setDialogOpen(true);
    };

    const handleDelete = async (row) => {
        const n = normalizeModelConfigRow(row);
        const id = n.modelConfigurationId;
        if (id == null || id === '') {
            toast.error('Missing configuration id; cannot delete.');
            return;
        }
        const label = (n.displayName || '').trim() || `id ${id}`;
        if (
            !window.confirm(
                `Delete model configuration "${label}"? Agents that use it will have their LLM profile cleared until you assign another.`
            )
        ) {
            return;
        }
        const tid = getTenantId();
        try {
            const res = await fetch(
                `${getBackendBase()}asrosync/model-configurations/tenant/${encodeURIComponent(tid)}/${encodeURIComponent(id)}`,
                { method: 'DELETE', headers: getAuthHeaders() }
            );
            if (!res.ok) {
                const t = await res.text();
                let msg = t;
                try {
                    const j = JSON.parse(t);
                    msg = j.error || t;
                } catch {
                    /* ignore */
                }
                throw new Error(msg || `HTTP ${res.status}`);
            }
            toast.success('Model configuration deleted');
            fetchRows();
        } catch (e) {
            console.error(e);
            toast.error(e.message || 'Delete failed');
        }
    };

    const parseConnectionConfig = () => {
        const raw = (form.connectionConfig || '').trim();
        if (!raw) return {};
        try {
            const o = JSON.parse(raw);
            return typeof o === 'object' && o !== null && !Array.isArray(o) ? o : {};
        } catch {
            throw new Error('connection_config must be valid JSON object');
        }
    };

    const save = async () => {
        const tid = getTenantId();
        const inv = (form.invocationKind || '').trim();
        const name = (form.displayName || '').trim();
        if (!form.modelKey) {
            toast.error('Select a model from the dropdown');
            return;
        }
        if (!name) {
            toast.error('Display name is required');
            return;
        }
        if (!inv) {
            toast.error(
                'Selected model has no invocation_kind — fix it under CDX → Master LLM'
            );
            return;
        }
        if (!editingId && !form.showSecretMask && !(form.apiSecret || '').trim()) {
            if (inv !== 'ollama_chat') {
                toast.error('API secret is required for this provider');
                return;
            }
        }
        let connectionConfig;
        try {
            connectionConfig = parseConnectionConfig();
        } catch (err) {
            toast.error(err.message);
            return;
        }
        if (!connectionConfig.model && !connectionConfig.model_code) {
            toast.error('Selected model did not fill connection_config.model');
            return;
        }
        const body = {
            display_name: name,
            invocation_kind: inv,
            connection_config: connectionConfig,
            is_active: form.isActive,
            created_by: 'model_config_ui',
            modified_by: 'model_config_ui',
        };
        if (form.apiSecret && form.apiSecret.trim()) {
            body.api_secret = form.apiSecret.trim();
        }
        try {
            const url = editingId
                ? `${getBackendBase()}asrosync/model-configurations/tenant/${encodeURIComponent(tid)}/${editingId}`
                : `${getBackendBase()}asrosync/model-configurations/tenant/${encodeURIComponent(tid)}`;
            const res = await fetch(url, {
                method: editingId ? 'PUT' : 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const t = await res.text();
                let msg = t;
                try {
                    const j = JSON.parse(t);
                    msg = j.error || t;
                } catch {
                    /* ignore */
                }
                throw new Error(msg);
            }
            toast.success(editingId ? 'Updated' : 'Created');
            setDialogOpen(false);
            fetchRows();
        } catch (e) {
            console.error(e);
            toast.error(e.message || 'Save failed');
        }
    };

    const paged = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const selected = dropdownOptions.find((r) => llmOptionKey(r) === form.modelKey);

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Psychology color="primary" />
                <Typography variant="h5">Model configuration</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Pick from models licensed for this tenant (CDX → License → Licensed LLMs). Invocation and
                connection defaults fill from Master LLM — you only add display name and API secret.
            </Typography>
            {catalogLoaded && hasLicensedLlms && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Licensed for this tenant:{' '}
                    {dropdownOptions.map((r) => r.displayName).join(', ')}.
                </Alert>
            )}
            {catalogLoaded && !hasLicensedLlms && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    No Licensed LLMs on this subscription. Ask CDX to add models under{' '}
                    <strong>License → Licensed LLMs</strong> (and Save), then refresh this page.
                </Alert>
            )}
            <Box sx={{ mb: 2 }}>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={openCreate}
                    disabled={catalogLoaded && !hasLicensedLlms}
                >
                    Add model configuration
                </Button>
            </Box>
            <Paper>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Invocation</TableCell>
                                <TableCell>Secret</TableCell>
                                <TableCell>Active</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <CircularProgress size={28} />
                                    </TableCell>
                                </TableRow>
                            ) : paged.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        No rows
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paged.map((r) => (
                                    <TableRow key={r.modelConfigurationId}>
                                        <TableCell>{r.displayName}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={r.invocationKind} />
                                        </TableCell>
                                        <TableCell>{r.hasApiSecret ? 'Yes' : 'No'}</TableCell>
                                        <TableCell>{r.isActive ? 'Yes' : 'No'}</TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                aria-label="Edit"
                                                onClick={() => openEdit(r)}
                                                color="primary"
                                            >
                                                <Edit fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                aria-label="Delete"
                                                onClick={() => handleDelete(r)}
                                                color="error"
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={rows.length}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingId ? 'Edit model configuration' : 'Add model configuration'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel id="model-pick-label">Model</InputLabel>
                                <Select
                                    labelId="model-pick-label"
                                    label="Model"
                                    value={form.modelKey}
                                    onChange={(e) => applyModel(e.target.value)}
                                >
                                    {dropdownOptions.map((r) => {
                                        const key = llmOptionKey(r);
                                        return (
                                            <MenuItem key={key} value={key}>
                                                {r.displayName}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>

                        {selected && (
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    <Chip
                                        size="small"
                                        label={`Invocation: ${form.invocationKind || '—'}`}
                                    />
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={`Model: ${selected.modelCode}`}
                                    />
                                </Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ mt: 0.75 }}
                                >
                                    Filled from Master LLM — enter display name and API secret only.
                                </Typography>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                label="Display name"
                                value={form.displayName}
                                onChange={(e) => handleField('displayName', e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                type="password"
                                label="API secret"
                                required={!editingId && form.invocationKind !== 'ollama_chat'}
                                value={form.showSecretMask ? SECRET_MASK_PLACEHOLDER : form.apiSecret}
                                onFocus={() => {
                                    if (form.showSecretMask) {
                                        setForm((prev) => ({
                                            ...prev,
                                            showSecretMask: false,
                                            apiSecret: '',
                                        }));
                                    }
                                }}
                                onChange={(e) => handleField('apiSecret', e.target.value)}
                                helperText={
                                    editingId
                                        ? form.showSecretMask
                                            ? 'Secret stored (masked). Click to replace, or leave as-is on save.'
                                            : 'Leave blank to keep the current secret.'
                                        : 'Your tenant API key — never stored on Master LLM.'
                                }
                                autoComplete="new-password"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.isActive}
                                        onChange={(e) => handleField('isActive', e.target.checked)}
                                    />
                                }
                                label="Active"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Button
                                size="small"
                                startIcon={showAdvanced ? <ExpandLess /> : <ExpandMore />}
                                onClick={() => setShowAdvanced((v) => !v)}
                            >
                                {showAdvanced ? 'Hide' : 'Show'} advanced connection JSON
                            </Button>
                            <Collapse in={showAdvanced}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={5}
                                    sx={{ mt: 1 }}
                                    label="connection_config (JSON)"
                                    value={form.connectionConfig}
                                    onChange={(e) => handleField('connectionConfig', e.target.value)}
                                    InputProps={{ sx: { fontFamily: 'monospace' } }}
                                    helperText="Defaults from Master LLM. Override base_url / options only if needed."
                                />
                            </Collapse>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={save}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ModelConfigurationAdmin;
